/**
 * Regulatory Requirements Import System
 * Imports vessel requirements from the vessel calculator JSON export
 * Maps requirements to FleetSkipper's maintenance, drills, and documents modules
 */

/**
 * Import vessel requirements from calculator export
 * @param {Object} exportData - JSON export from vessel calculator
 * @returns {Object} Mapped requirements for FleetSkipper
 */
export function importVesselRequirements(exportData) {
  if (!exportData || !exportData.requirements) {
    throw new Error('Invalid export data format');
  }

  const { vessel, requirements, timestamp } = exportData;
  
  return {
    vesselConfig: mapVesselConfig(vessel),
    importDate: timestamp || new Date().toISOString(),
    summary: {
      totalRequirements: exportData.summary?.total || 0,
      mandatoryCount: exportData.summary?.mandatory || 0,
    },
    equipment: mapEquipment(requirements.equipment || []),
    maintenance: mapMaintenance(requirements.maintenance || []),
    drills: mapDrills(requirements.drills || []),
    documents: mapDocuments(requirements.documents || []),
  };
}

/**
 * Map vessel configuration from calculator to FleetSkipper format
 */
function mapVesselConfig(vessel) {
  if (!vessel) return {};
  
  return {
    name: vessel.vesselName || 'Unnamed Vessel',
    type: vessel.vesselType,
    category: vessel.category,
    operatingArea: vessel.operatingArea,
    length: vessel.length,
    grossTonnage: vessel.grossTonnage,
    pob: vessel.pob,
    crew: vessel.crew,
    engineType: vessel.engineType,
    enginePower: vessel.enginePower,
    features: {
      hasAccommodation: vessel.hasAccommodation,
      hasSleeping: vessel.hasSleeping,
      hasCooking: vessel.hasCooking,
      hasPassengers: vessel.hasPassengers,
      hasDangerousGoods: vessel.hasDangerousGoods,
      hasTowingOps: vessel.hasTowingOps,
      hasLiferaft: vessel.hasLiferaft,
      hasEPIRB: vessel.hasEPIRB,
    }
  };
}

/**
 * Map equipment requirements to FleetSkipper equipment checklist format
 */
function mapEquipment(equipment) {
  return equipment.map(item => ({
    id: item.equipment_id,
    name: item.ui_label || item.category,
    category: item.category,
    domain: item.domain,
    mandatory: item.classification === 'MANDATORY',
    quantity: item.quantity_formula || '1',
    regulation: item.reg_refs,
    notes: item.notes || item.evidence,
    importedFrom: 'vessel-calculator',
    importDate: new Date().toISOString(),
    // FleetSkipper specific fields
    status: 'pending', // pending, installed, verified
    lastChecked: null,
    nextCheckDue: null,
    location: null,
    serialNumber: null,
  }));
}

/**
 * Map maintenance requirements to FleetSkipper maintenance schedule format
 */
function mapMaintenance(maintenance) {
  return maintenance.map(item => ({
    id: item.task_id,
    taskName: item.task_title || item.task_id,
    parentSystem: item.parent_id || 'General',
    domain: item.domain,
    mandatory: item.classification === 'MANDATORY',
    frequency: parseFrequency(item.frequency),
    regulation: item.reg_refs,
    notes: item.notes,
    importedFrom: 'vessel-calculator',
    importDate: new Date().toISOString(),
    // FleetSkipper specific fields
    lastCompleted: null,
    nextDue: calculateNextDue(item.frequency),
    assignedTo: null,
    status: 'scheduled', // scheduled, overdue, completed
  }));
}

/**
 * Map drill requirements to FleetSkipper drills module format
 */
function mapDrills(drills) {
  // Separate actual drills from training/monitoring
  const actualDrills = [];
  const training = [];
  
  drills.forEach(item => {
    const drillName = item.drill_name || '';
    const isActualDrill = 
      drillName.toLowerCase().includes('drill') ||
      drillName.toLowerCase().includes('muster') ||
      drillName.toLowerCase().includes('abandon') ||
      drillName.toLowerCase().includes('fire') ||
      drillName.toLowerCase().includes('overboard');
    
    const mapped = {
      id: item.drill_id,
      name: item.drill_name,
      description: item.drill_description,
      domain: item.domain,
      mandatory: item.classification === 'MANDATORY',
      frequency: parseFrequency(item.frequency),
      regulation: item.reg_refs,
      notes: item.notes,
      importedFrom: 'vessel-calculator',
      importDate: new Date().toISOString(),
      // FleetSkipper specific fields
      lastConducted: null,
      nextDue: calculateNextDue(item.frequency),
      participants: [],
      outcome: null, // pass, fail, partial
    };
    
    if (isActualDrill) {
      actualDrills.push({ ...mapped, type: 'drill' });
    } else {
      training.push({ ...mapped, type: 'training' });
    }
  });
  
  return {
    drills: actualDrills,
    training: training,
  };
}

/**
 * Map document requirements to FleetSkipper document management format
 */
function mapDocuments(documents) {
  const categorized = {
    policies: [],
    procedures: [],
    riskAssessments: [],
    certificates: [],
    records: [],
    manuals: [],
    other: [],
  };
  
  documents.forEach(item => {
    const docName = (item.doc_name || '').toLowerCase();
    const mapped = {
      id: item.doc_id,
      name: item.doc_name,
      description: item.doc_description,
      domain: item.domain,
      mandatory: item.classification === 'MANDATORY',
      retentionPeriod: item.retention_period,
      regulation: item.reg_refs,
      notes: item.notes,
      importedFrom: 'vessel-calculator',
      importDate: new Date().toISOString(),
      // FleetSkipper specific fields
      status: 'required', // required, draft, approved, archived
      currentVersion: null,
      lastReviewed: null,
      nextReviewDue: calculateDocumentReview(item.retention_period),
      location: null, // URL or file path
      responsible: null,
    };
    
    // Categorize document
    if (docName.includes('risk assessment')) {
      categorized.riskAssessments.push(mapped);
    } else if (docName.includes('policy')) {
      categorized.policies.push(mapped);
    } else if (docName.includes('procedure') || docName.includes('checklist')) {
      categorized.procedures.push(mapped);
    } else if (docName.includes('certificate') || docName.includes('license')) {
      categorized.certificates.push(mapped);
    } else if (docName.includes('record') || docName.includes('log')) {
      categorized.records.push(mapped);
    } else if (docName.includes('manual') || docName.includes('plan')) {
      categorized.manuals.push(mapped);
    } else {
      categorized.other.push(mapped);
    }
  });
  
  return categorized;
}

/**
 * Parse frequency string to structured format
 */
function parseFrequency(frequency) {
  if (!frequency) return { type: 'as-required' };
  
  const freq = frequency.toLowerCase();
  
  if (freq.includes('monthly') || freq === 'monthly') {
    return { type: 'recurring', interval: 1, unit: 'month' };
  } else if (freq.includes('quarterly') || freq === 'quarterly') {
    return { type: 'recurring', interval: 3, unit: 'month' };
  } else if (freq.includes('semiannual') || freq.includes('6 month')) {
    return { type: 'recurring', interval: 6, unit: 'month' };
  } else if (freq.includes('annual') || freq.includes('yearly')) {
    return { type: 'recurring', interval: 12, unit: 'month' };
  } else if (freq.includes('week')) {
    const match = freq.match(/(\d+)\s*week/);
    const weeks = match ? parseInt(match[1]) : 1;
    return { type: 'recurring', interval: weeks, unit: 'week' };
  } else if (freq.includes('day')) {
    const match = freq.match(/(\d+)\s*day/);
    const days = match ? parseInt(match[1]) : 1;
    return { type: 'recurring', interval: days, unit: 'day' };
  } else if (freq.includes('continuous')) {
    return { type: 'continuous' };
  } else {
    return { type: 'as-required', original: frequency };
  }
}

/**
 * Calculate next due date based on frequency
 */
function calculateNextDue(frequency) {
  const parsed = parseFrequency(frequency);
  if (parsed.type !== 'recurring') return null;
  
  const now = new Date();
  const { interval, unit } = parsed;
  
  switch (unit) {
    case 'day':
      now.setDate(now.getDate() + interval);
      break;
    case 'week':
      now.setDate(now.getDate() + (interval * 7));
      break;
    case 'month':
      now.setMonth(now.getMonth() + interval);
      break;
    case 'year':
      now.setFullYear(now.getFullYear() + interval);
      break;
  }
  
  return now.toISOString();
}

/**
 * Calculate document review date based on retention period
 */
function calculateDocumentReview(retentionPeriod) {
  if (!retentionPeriod || retentionPeriod.toLowerCase() === 'current') {
    // Review annually by default
    const date = new Date();
    date.setFullYear(date.getFullYear() + 1);
    return date.toISOString();
  }
  
  // Parse retention period for review frequency
  const period = retentionPeriod.toLowerCase();
  if (period.includes('year')) {
    const match = period.match(/(\d+)\s*year/);
    const years = match ? parseInt(match[1]) : 1;
    const date = new Date();
    date.setFullYear(date.getFullYear() + Math.min(years, 1)); // Review at least annually
    return date.toISOString();
  }
  
  // Default annual review
  const date = new Date();
  date.setFullYear(date.getFullYear() + 1);
  return date.toISOString();
}

/**
 * Apply imported requirements to FleetSkipper modules
 * Now async to prevent UI blocking
 */
export async function applyImportedRequirements(importedData) {
  const results = {
    equipment: 0,
    maintenance: 0,
    drills: 0,
    documents: 0,
    errors: [],
  };
  
  try {
    // Add small delays to prevent UI blocking
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));
    // Save vessel configuration
    if (importedData.vesselConfig) {
      const existingVessel = JSON.parse(localStorage.getItem('vesselConfig') || '{}');
      const mergedVessel = { ...existingVessel, ...importedData.vesselConfig };
      localStorage.setItem('vesselConfig', JSON.stringify(mergedVessel));
    }
    
    await delay(10); // Prevent UI blocking
    
    // Import equipment to the correct key that EquipmentRecordsTab expects
    if (importedData.equipment?.length > 0) {
      // Check if vessel-scoped storage is being used
      const vesselId = importedData.vesselConfig?.id || 'default';
      const equipmentKey = `equipment_register_${vesselId}`;
      
      const existing = JSON.parse(localStorage.getItem(equipmentKey) || '[]');
      const merged = mergeUnique(existing, importedData.equipment, 'id');
      localStorage.setItem(equipmentKey, JSON.stringify(merged));
      
      // Trigger storage event so React components update
      window.dispatchEvent(new CustomEvent('sr:storage:set', { 
        detail: {
          module: 'equipment_register',
          vesselId,
          key: equipmentKey,
          value: merged,
          timestamp: Date.now(),
          backend: 'localStorage',
          resolvedKey: equipmentKey,
          baseKey: 'equipment_register'
        }
      }));
      
      results.equipment = importedData.equipment.length;
    }
    
    await delay(10); // Prevent UI blocking
    
    // Import maintenance
    if (importedData.maintenance?.length > 0) {
      // Use vessel-scoped key for maintenance
      const vesselId = importedData.vesselConfig?.id || 'default';
      const maintenanceKey = `maintenanceSchedule_${vesselId}`;
      
      const existing = JSON.parse(localStorage.getItem(maintenanceKey) || '[]');
      const merged = mergeUnique(existing, importedData.maintenance, 'id');
      localStorage.setItem(maintenanceKey, JSON.stringify(merged));
      
      // Trigger storage event so React components update
      window.dispatchEvent(new CustomEvent('sr:storage:set', { 
        detail: {
          module: 'maintenanceSchedule',
          vesselId,
          key: maintenanceKey,
          value: merged,
          timestamp: Date.now(),
          backend: 'localStorage',
          resolvedKey: maintenanceKey,
          baseKey: 'maintenanceSchedule'
        }
      }));
      
      results.maintenance = importedData.maintenance.length;
    }
    
    await delay(10); // Prevent UI blocking
    
    // Import drills
    if (importedData.drills?.drills?.length > 0) {
      const vesselId = importedData.vesselConfig?.id || 'default';
      const drillsKey = `drills_${vesselId}`;
      
      const existing = JSON.parse(localStorage.getItem(drillsKey) || '[]');
      const merged = mergeUnique(existing, importedData.drills.drills, 'id');
      localStorage.setItem(drillsKey, JSON.stringify(merged));
      
      // Trigger storage event so React components update
      window.dispatchEvent(new CustomEvent('sr:storage:set', { 
        detail: {
          module: 'drills',
          vesselId,
          key: drillsKey,
          value: merged,
          timestamp: Date.now(),
          backend: 'localStorage',
          resolvedKey: drillsKey,
          baseKey: 'drills'
        }
      }));
      
      results.drills = importedData.drills.drills.length;
    }
    
    // Import training
    if (importedData.drills?.training?.length > 0) {
      const vesselId = importedData.vesselConfig?.id || 'default';
      const trainingKey = `trainingRequirements_${vesselId}`;
      
      const existing = JSON.parse(localStorage.getItem(trainingKey) || '[]');
      const merged = mergeUnique(existing, importedData.drills.training, 'id');
      localStorage.setItem(trainingKey, JSON.stringify(merged));
      
      // Trigger storage event so React components update
      window.dispatchEvent(new CustomEvent('sr:storage:set', { 
        detail: {
          module: 'trainingRequirements',
          vesselId,
          key: trainingKey,
          value: merged,
          timestamp: Date.now(),
          backend: 'localStorage',
          resolvedKey: trainingKey,
          baseKey: 'trainingRequirements'
        }
      }));
    }
    
    // Import documents (all categories)
    if (importedData.documents) {
      let docCount = 0;
      Object.keys(importedData.documents).forEach(category => {
        const docs = importedData.documents[category];
        if (docs?.length > 0) {
          const storageKey = `documents_${category}`;
          const existing = JSON.parse(localStorage.getItem(storageKey) || '[]');
          const merged = mergeUnique(existing, docs, 'id');
          localStorage.setItem(storageKey, JSON.stringify(merged));
          docCount += docs.length;
        }
      });
      results.documents = docCount;
    }
    
    // Store import history
    const importHistory = JSON.parse(localStorage.getItem('importHistory') || '[]');
    importHistory.push({
      date: importedData.importDate,
      vessel: importedData.vesselConfig?.name,
      summary: results,
    });
    localStorage.setItem('importHistory', JSON.stringify(importHistory));
    
  } catch (error) {
    results.errors.push(error.message);
  }
  
  return results;
}

/**
 * Merge arrays avoiding duplicates based on unique key
 */
function mergeUnique(existing, imported, uniqueKey) {
  const map = new Map();
  
  // Add existing items
  existing.forEach(item => {
    if (item[uniqueKey]) {
      map.set(item[uniqueKey], item);
    }
  });
  
  // Add or update with imported items
  imported.forEach(item => {
    if (item[uniqueKey]) {
      map.set(item[uniqueKey], item);
    }
  });
  
  return Array.from(map.values());
}

export default {
  importVesselRequirements,
  applyImportedRequirements,
};